import { OscarOrgDetails, Status } from "./types/models";
import API from "./Api";

type EmptyObject = Record<string, never>;

type PagingInfo = {
  total_results: number;
  offset: number;
  limit: number;
  next_offset: number | null;
};

export type SignalFilters = {
  keywords: string[];
  partners: string[];
  competitors: string[];
};

export type SearchResponse<R> = {
  results: R[];
  paging_info: PagingInfo;
};

type SearchOscarOrgsParams = {
  offset?: number;
  limit?: number;
  sort?: string;
  sort_order?: "ASC" | "DESC";
  text?: string;
  id?: string[];
};

type SearchOscarOrgsResponse = SearchResponse<OscarOrgDetails>;

export enum SignalCategory {
  KEYWORD = "Keywords",
  PARTNER = "Partners",
  COMPETITOR = "Competitors",
  CPV_CODE = "CPV codes",
}
type SignalId = string & { __type_tag: "SignalId" };

export type Signal = {
  id: SignalId;
  category: string;
  name: string;
  signal_criteria?: SignalCriteria;
};

type SignalCriteria = {
  keyword?: string;
  organisation_id?: string;
  organisation_name?: string;
  cpv_code?: string;
};

export type SelfServeQuery = {
  countries: string[];
  languages: string[] | undefined;
  keywords: {
    keywords: string[];
    from_date: Date;
    status: Status[];
  };
  partners?: {
    org_ids: string[];
    from_date: Date;
  };
  competitors?: {
    org_ids: string[];
    from_date: Date;
  };
  buyers?: {
    buyer_ids: number[];
    from_date: Date;
    sectors?: string[];
    sectors_include_null?: boolean;
    keywords: string[];
    cpv_codes: string[];
    cpv_codes_include_null?: boolean;
  };
  cpv_codes?: {
    codes: string[];
  };
};

export type OwnerType = "BUYER" | "RECORD" | "SUPPLIER";

class StotlesAPI extends API {
  constructor(endpoint: string = window.location.origin) {
    super(endpoint);
  }

  searchOscarOrgs(params: SearchOscarOrgsParams): Promise<SearchResponse<OscarOrgDetails>> {
    return this.fetchJSON<EmptyObject, SearchOscarOrgsResponse>("GET", `api/oscar_orgs`, {
      query: this.toURLQueryParams(params),
    });
  }

  toURLQueryParams<Input extends Record<string, any>>(input: Input): Record<string, string> {
    const queryParams: Record<string, string> = {};

    for (const key of Object.keys(input)) {
      const value = input[key];
      if (value === undefined) {
        continue;
      }
      if (value === null) {
        queryParams[key] = "";
      } else if (Array.isArray(value)) {
        queryParams[key] = value.map((e) => e.toString()).join(",");
      } else if (typeof value === "boolean") {
        queryParams[key] = value ? "Y" : "N";
      } else if (typeof value !== "string") {
        queryParams[key] = value.toString();
      } else {
        queryParams[key] = value;
      }
    }

    return queryParams;
  }

  /**
   * Standardise and sanitise a body for a post request to make sure it is uniform
   */
  private sanitiseBody(input: Record<string, any>) {
    const body: Record<string, any> = {};

    for (const [key, value] of Object.entries(input)) {
      // consistency with toURLQueryParams
      if (value === undefined) {
        continue;
      } else if (Array.isArray(value)) {
        // for the time being we will also keep this consistent with GET requests, for the benefit
        // of existing params. Back end changes will be required to adjust how Rails permits
        // existing parameters
        body[key] = value.map((e) => e.toString()).join(",");
      } else {
        body[key] = value;
      }
    }

    return body;
  }
}

export default StotlesAPI;
